import { DeleteOutline } from '@mui/icons-material'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import { Context, useMemo } from 'react'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCertificateCache, useDeleteCertificate, useGetCertificates } from 'src/api/Certificates'
import { invalidateMainComponentCache } from 'src/api/main-component'
import AccordionCommon from 'src/components/AccordionCommon'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import GenericTable from 'src/components/table/GenericTable'
import { MainComponentStatus } from 'src/enums'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { coreUtils, getThemeColor } from 'src/utils'
import NoDocument from '../../../../assets/svg/no-equipment-documents.svg'
import { AccordionDocumentsTable } from '../../documents/components/AccordionDocumentsTable'
import { InteractiveSchemaReportsInDocuments } from './InteractiveSchemaReportsInDocuments'
import { useGetReportsByMainComponentId } from 'src/api/useInteractiveSchema'
import { ReportStatus } from 'src/api/interactive-schema-types'

interface Props {
  context: Context<any> | null | undefined
  customerId: EntityId
  localityId: EntityId | undefined
  mainComponentId: any
  mainComponent: IMainComponent | undefined
  autoheight: boolean
  maximumContentHeight: number
  minimalEmptyStatus: boolean
  documentCount: number
  setDataLength?: (length: number) => void
  isReadOnly?: boolean
  contentRef?: React.Ref<HTMLDivElement>
}

const DocumentsInEquipment = (props: Props) => {
  const {
    context,
    customerId,
    localityId,
    mainComponentId,
    mainComponent,
    minimalEmptyStatus,
    autoheight,
    maximumContentHeight,
    documentCount,
    setDataLength,
    isReadOnly,
    contentRef,
  } = props
  const { data: certificates } = useGetCertificates(mainComponentId, { includeDeleted: true })
  const historicalCertificates = useMemo(() => {
    return [...(certificates?.items || [])]
      .filter(c => !c?.isValid)
      .sort((a, b) => {
        const aTime = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const bTime = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0
        return bTime - aTime
      })
  }, [certificates])
  const theme = useTheme()
  const { data: allInteractiveReports = [] } = useGetReportsByMainComponentId(mainComponentId)
  const interactiveReports = allInteractiveReports.filter(
    r => r.status === ReportStatus.Completed || r.status === ReportStatus.Approved,
  )

  const hasAdditional = historicalCertificates.length > 0 || interactiveReports.length > 0

  const additionalComponent = hasAdditional ? (
    <>
      {historicalCertificates.length > 0 && (
        <CertificateSection
          mainComponentId={mainComponentId}
          customerId={customerId}
          context={context}
          mainComponent={mainComponent}
          certificates={historicalCertificates}
        />
      )}
      {interactiveReports.length > 0 && <InteractiveSchemaReportsInDocuments mainComponentId={mainComponentId} />}
    </>
  ) : null

  const isEmpty = documentCount === 0 && !hasAdditional

  return (
    <DocumentsContainer isEmpty={isEmpty}>
      <AccordionDocumentsTable
        customerId={customerId as EntityId}
        localityId={localityId as EntityId}
        documentTypeId=""
        documentsParams={{ mainComponentId, customerId: customerId as EntityId }}
        removeBoxShadow={true}
        context={context as Context<any>}
        emptyStatusTitle={t('equipment-document-empty-status-title', { ns: 'equipment' })}
        emptyStatusContent={t('equipment-document-empty-status-content', { ns: 'equipment' })}
        emptyStatusImageWidth="100px"
        emptyStatusHeight="100%"
        emptyStatusImage={NoDocument}
        emptyStatusContentFontSize="14px"
        emptyStatusTitleFontSize="18px"
        equipmentStatus={mainComponent?.status}
        imageContainerClasses="pb-4 pt-0"
        contentTopMargin="10px"
        emptyStatusContentClass="mt-2"
        spinnerHeight={100}
        hideColumns={['mooring', 'dueDate']}
        additionalComponent={additionalComponent}
        tableRowHeight={36}
        actionButtonheightAndWidth="24px"
        iconWidth="16px"
        accordionBackgroundColor={`${getThemeColor(theme, MUIThemeColors.backgroundMain)} !important`}
        accordionBorderStyle={`1px dashed ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
        expandedBottomMargin="4px"
        height={'auto'}
        autoHeight={autoheight}
        maximumContentHeight={maximumContentHeight}
        minimalEmptyStatus={minimalEmptyStatus}
        setDataLength={setDataLength}
        disabled={isReadOnly}
        containerRef={contentRef}
      />
    </DocumentsContainer>
  )
}

const CertificateSection = ({ mainComponentId, customerId, context, mainComponent, certificates }) => {
  const theme = useTheme()
  const { openFilePreviewDialog, showConfirmDialog } = useDialogCtx()
  const { mutateAsync, isLoading: isDeleting } = useDeleteCertificate(mainComponentId)

  const columns: GridColumns = [
    {
      field: 'certificateNumber',
      headerName: t('cert.NR  ', { ns: 'certificate' }).toUpperCase(),
      flex: 1,
      cellClassName: 'font-weight-semi-bold',
      renderCell: params => (
        <Typography variant="body2" className="font-weight-semi-bold">
          {params.value} | {params.row.supplierName} | {coreUtils.getFormattedDate(params.row.validUntil)} | #
          {params.row.numberOfExtensions}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Flex.Row>
          {mainComponent.mainComponentStatus == MainComponentStatus.InService && (
            <AuthorizationProvider
              Context={context}
              permissions={[
                permissionValue.Locality.Mooring.Write,
                permissionValue.ServiceStation.Update,
                permissionValue.ClientAdminPortal.Equipment_Portal,
              ]}
              type={AuthorizationType.Disabled}
            >
              <GridActionButton
                variant="outlined"
                buttonIcon={<DeleteOutline />}
                disabled={params.row.isDeletable || isDeleting}
                onClick={() => onDeleteCertificate(params.row.id)}
                height="36px"
                width="36px"
                shadowWeight={0}
                sx={{ opacity: !params.row?.isValid && (!params.row.isDeletable || isDeleting) ? 0.5 : 1 }}
              />
            </AuthorizationProvider>
          )}
        </Flex.Row>
      ),
    },
  ]

  const onDeleteCertificate = (id: EntityId) => {
    showConfirmDialog(
      t('deleteCertificate', { ns: 'certificate' }),
      t('areYouSureDeleteCertificate', { ns: 'certificate' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await mutateAsync(id, {
          onSuccess: () => {
            toast.success(t('CertificateDeletedSuccussfully', { ns: 'certificate' }))
            invalidateCertificateCache.getCertificates(mainComponentId)
            invalidateMainComponentCache.getMainComponentById(customerId, mainComponentId)
            invalidationActivityLogCache.getCageActivityLogs()
            pop()
          },
          onError: () => {
            toast.success(t('ErrorDeletingCertificate', { ns: 'certificate' }))
          },
        })
      },
    )
  }

  return (
    <AccordionCommon
      accordionHeight="32px !important"
      iconColor={MUIThemeColors.secondaryDark}
      title={
        <Flex.Row className="p-8-core" justifyContent={'space-between'} alignItems={'center'} width="100%" height={32}>
          <DynamicTextTypography
            maxLength={25}
            variant="subtitle2"
            fontWeight={500}
            text={t('historical-certificates', { ns: 'main-component' }).toUpperCase()}
            fontSize={12}
          />
        </Flex.Row>
      }
      borderRadius="0px !important"
      bgColor={`${getThemeColor(theme, MUIThemeColors.backgroundMain)} !important`}
      removeBoxShadow={true}
      border={`1px dashed ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
    >
      <GenericTable
        id="certificate-table"
        columns={columns}
        data={certificates ?? []}
        hideColumnHeaders
        onRowClick={params => openFilePreviewDialog(params?.row?.fileUrl)}
        autoHeight
        getRowHeight={() => 36}
        minRowHeight="36px"
      />
    </AccordionCommon>
  )
}

export default DocumentsInEquipment

const DocumentsContainer = styled(Box, { shouldForwardProp: prop => prop !== 'isEmpty' })<{ isEmpty: boolean }>(
  ({ isEmpty }) => ({
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    ...(isEmpty && { display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '10px' }),
  }),
)
